import React, { useRef, useState, useEffect, useCallback } from 'react';

/**
 * GlobalZoomPan — WhatsApp-Web-style zoom + pan for the entire app on desktop.
 *
 * • Ctrl / Cmd + wheel → smooth zoom around cursor
 * • Trackpad pinch (Chrome/Safari fires wheel with ctrlKey=true) → same zoom
 * • Two-finger drag / touch pan / middle-click drag → pan when scale > 1
 * • Double-tap `Ctrl+0` → reset
 *
 * The wrapper is a no-op on mobile (viewport width < 768px) so the PWA gesture
 * story stays untouched.
 */
const MIN_SCALE = 1;
const MAX_SCALE = 4;

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const isDesktop = () => typeof window !== 'undefined' && window.innerWidth >= 768 && !('ontouchstart' in window);

const GlobalZoomPan = ({ children }) => {
  const [enabled, setEnabled] = useState(isDesktop());
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const wrapperRef = useRef(null);
  const panRef = useRef(null);

  // Keep the enabled flag in sync with viewport size (window resize / device rotation).
  useEffect(() => {
    const onResize = () => setEnabled(isDesktop());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Clamp translation so we can't drag content entirely off-screen.
  const clampTranslate = useCallback((x, y, s) => {
    if (!wrapperRef.current) return { x, y };
    const rect = wrapperRef.current.getBoundingClientRect();
    const maxX = (rect.width * (s - 1)) / 2 + 40;
    const maxY = (rect.height * (s - 1)) / 2 + 40;
    return { x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) };
  }, []);

  const resetTransform = useCallback(() => {
    setScale(1); setTx(0); setTy(0);
  }, []);

  // Keyboard: Ctrl/Cmd+0 → reset
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        resetTransform();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [resetTransform]);

  // Ctrl/Cmd + wheel → zoom around cursor; trackpad pinch fires this too.
  useEffect(() => {
    if (!enabled) return;
    const el = wrapperRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;  // let normal scrolling pass through
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      const delta = -e.deltaY * 0.005;
      setScale((s) => {
        const nextScale = clamp(s + delta * s, MIN_SCALE, MAX_SCALE);
        // Keep the pointer position stable: translate so that (cx, cy) in the
        // scaled coordinate frame stays anchored under the cursor.
        const factor = nextScale / s;
        setTx((prev) => (prev - cx) * factor + cx);
        setTy((prev) => (prev - cy) * factor + cy);
        if (nextScale === 1) { setTx(0); setTy(0); }
        return nextScale;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [enabled]);

  // Pan when zoomed in — middle-click drag OR Alt+left-click drag OR touch drag
  const onMouseDown = (e) => {
    if (!enabled || scale <= 1) return;
    // Only pan on middle button, alt+left, or Ctrl+left (avoid stealing normal clicks)
    if (e.button !== 1 && !(e.button === 0 && (e.altKey || e.ctrlKey))) return;
    e.preventDefault();
    panRef.current = { startX: e.clientX, startY: e.clientY, startTx: tx, startTy: ty };
    const onMove = (ev) => {
      if (!panRef.current) return;
      const nx = panRef.current.startTx + (ev.clientX - panRef.current.startX);
      const ny = panRef.current.startTy + (ev.clientY - panRef.current.startY);
      const { x, y } = clampTranslate(nx, ny, scale);
      setTx(x); setTy(y);
    };
    const onUp = () => {
      panRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Touchpad two-finger drag on macOS Safari fires wheel events without Ctrl.
  // We use those to pan the zoomed viewport when scale > 1.
  useEffect(() => {
    if (!enabled) return;
    const el = wrapperRef.current;
    if (!el) return;
    const onWheelPan = (e) => {
      if (e.ctrlKey || e.metaKey) return;  // zoom handled elsewhere
      if (scale <= 1) return;  // no need to pan when not zoomed
      // On macOS trackpad the deltas are typically small and continuous.
      // Only intercept when the user is clearly two-finger dragging horizontally
      // — otherwise vertical scroll on a scrollable panel should still work.
      if (Math.abs(e.deltaX) < 1 && Math.abs(e.deltaY) < 1) return;
      // If shift held or deltaX dominates → treat as pan (feels natural on
      // trackpads that map horizontal swipes to deltaX).
      if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
        const { x, y } = clampTranslate(tx - e.deltaX, ty - e.deltaY, scale);
        setTx(x); setTy(y);
      }
    };
    el.addEventListener('wheel', onWheelPan, { passive: false });
    return () => el.removeEventListener('wheel', onWheelPan);
  }, [enabled, scale, tx, ty, clampTranslate]);

  if (!enabled) return <>{children}</>;

  return (
    <div
      ref={wrapperRef}
      onMouseDown={onMouseDown}
      style={{
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        position: 'relative',
        cursor: scale > 1 ? (panRef.current ? 'grabbing' : 'default') : 'default',
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
          transformOrigin: 'center center',
          transition: panRef.current ? 'none' : 'transform 120ms ease-out',
          willChange: 'transform',
        }}
      >
        {children}
      </div>
      {scale > 1.01 && (
        <button
          onClick={resetTransform}
          className="fixed bottom-4 right-4 z-[9999] bg-black/75 hover:bg-black text-white text-xs font-bold px-3 py-1.5 rounded-full backdrop-blur shadow-lg flex items-center gap-1.5"
          data-testid="global-zoom-reset"
          title="Reset zoom (Ctrl+0)"
        >
          {scale.toFixed(1)}× · Reset
        </button>
      )}
    </div>
  );
};

export default GlobalZoomPan;
